import ProfileAvatar from '../components/ProfileAvatar';
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_BASE_URL, WEBSITE_URL } from '../config';
import GetAppButton from '../components/GetAppButton';
import './AnnouncementDetail.css';

const AnnouncementDetail = () => {
    const { id } = useParams();
    const [announcement, setAnnouncement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [appRedirectAttempted, setAppRedirectAttempted] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-redirect to app on mobile using iframe approach to prevent page navigation
    useEffect(() => {
        if (isMobile && !appRedirectAttempted) {
            setAppRedirectAttempted(true);
            
            const deepLinkUrl = `myguc://announcement/${id}`;
            
            // Try to open the app using an invisible iframe
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = deepLinkUrl;
            document.body.appendChild(iframe);
            
            // Set a timer to detect if the app didn't open
            const startTime = Date.now();
            const checkTimer = setTimeout(() => {
                // If we're still on the page after 2 seconds, assume the app didn't open
                // In this case, we do nothing and let the user view the announcement on the web
                const timeElapsed = Date.now() - startTime;
                if (timeElapsed < 2500) {
                    // User likely doesn't have the app, stay on the web page
                    console.log('App not detected, showing web version');
                }
            }, 2000);
            
            // Listen for visibility change to detect if user switched to the app
            const handleVisibilityChange = () => {
                if (document.hidden) {
                    // User likely switched to the app
                    clearTimeout(checkTimer);
                }
            };
            
            document.addEventListener('visibilitychange', handleVisibilityChange);
            
            // Cleanup
            return () => {
                clearTimeout(checkTimer);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                if (iframe.parentNode) {
                    document.body.removeChild(iframe);
                }
            };
        }
    }, [isMobile, id, appRedirectAttempted]);

    useEffect(() => {
        fetch(`${API_BASE_URL}/announcements/${id}`)
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setAnnouncement(data.announcement);
                } else {
                    setError("Announcement not found");
                }
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setError("Network error");
                setLoading(false);
            });
    }, [id]);

    const handleShare = async () => {
        // Use the /share/:id endpoint which has Open Graph metadata for rich previews
        const shareUrl = `${WEBSITE_URL}/share/${id}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: announcement.title,
                    text: 'Check out this announcement on myGUNI!',
                    url: shareUrl,
                });
            } catch (err) {
                console.log('Share canceled or failed', err);
            }
        } else {
            navigator.clipboard.writeText(shareUrl).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
        }
    };

    const handleFileDownload = async (e, url) => {
        e.preventDefault();
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const fileName = url.split('/').pop() || 'download';

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download error:', error);
            window.open(url, '_blank');
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return new Intl.DateTimeFormat('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }).format(date);
    };

    if (loading) return (
        <div className="detail-loading">
            <div className="spinner"></div>
            <p>Loading...</p>
        </div>
    );

    if (error || !announcement) return (
        <div className="detail-error">
            <h2>{error || "Not Found"}</h2>
            <Link to="/announcements" className="back-link">Back to Announcements</Link>
        </div>
    );

    const viewCount = announcement.viewCount ?? announcement.viewedBy?.length ?? 0;
    const likeCount = announcement.likeCount ?? announcement.likedBy?.length ?? 0;

    return (
        <div className="announcement-detail-page">
            {/* Header */}
            <header className="announcements-header">
                <Link to="/announcements" className="header-back-button">
                    <div className="back-icon-circle">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M10 19L3 12L10 5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M3 12H15C18.3137 12 21 14.6863 21 18V19" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </div>
                </Link>

                <h1 className="header-title">Announcements</h1>

                <GetAppButton />
            </header>

            <div className="detail-content">
                <article className="detail-article">
                    {/* Image Section */}
                    <div className="detail-image-container">
                        {announcement.imageUrl ? (
                            <img
                                src={announcement.imageUrl}
                                alt={announcement.title}
                                className="detail-image"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}

                        {/* Fallback SVG */}
                        <div className={`detail-image-fallback ${announcement.imageUrl ? 'hidden' : ''}`}>
                            <svg
                                width="64"
                                height="64"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                        </div>
                    </div>

                    <div className="detail-body">
                        {/* Meta */}
                        <div className="detail-meta">
                            <span className={`detail-category category-${announcement.category || 'general'}`}>
                                {announcement.category || 'General'}
                            </span>

                            {announcement.uni && (
                                <span className={`detail-uni uni-${announcement.uni.toLowerCase()}`}>
                                    {announcement.uni}
                                </span>
                            )}
                            {(Array.isArray(announcement.targetMajor) ? announcement.targetMajor : announcement.targetMajor ? [announcement.targetMajor] : []).map(major => <span key={major} className="major-tag">{major}</span>)}
                        </div>

                        {/* Title */}
                        <h1 className="detail-title">{announcement.title}</h1>

                        {/* Author & Date */}
                        <div className="detail-author-date">
                            <span>{formatDate(announcement.createdAt)}</span>
                            <span>•</span>
                            <ProfileAvatar avatar={announcement.authorAvatar} /><span>{announcement.authorName || 'myGUNI Admin'}</span>
                        </div>

                        {/* Body */}
                        <div className="detail-text">
                            {announcement.body}
                        </div>

                        {/* Attachments */}
                        {(announcement.fileUrl || announcement.link) && (
                            <div className="detail-attachments">
                                <h3>Attachments</h3>
                                <div className="attachments-list">
                                    {announcement.fileUrl && (
                                        <a
                                            href={announcement.fileUrl}
                                            onClick={(e) => handleFileDownload(e, announcement.fileUrl)}
                                            className="attachment-item"
                                        >
                                            <div className="attachment-icon">
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F6B520" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9l-7-7z"></path>
                                                    <path d="M13 2v7h7"></path>
                                                </svg>
                                            </div>
                                            <div className="attachment-info">
                                                <div className="attachment-title">Document Attachment</div>
                                                <div className="attachment-subtitle">Download</div>
                                            </div>
                                        </a>
                                    )}
                                    {announcement.link && (
                                        <a
                                            href={announcement.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="attachment-item"
                                        >
                                            <div className="attachment-icon">
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F6B520" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"></path>
                                                    <polyline points="15 3 21 3 21 9"></polyline>
                                                    <line x1="10" y1="14" x2="21" y2="3"></line>
                                                </svg>
                                            </div>
                                            <div className="attachment-info">
                                                <div className="attachment-title">External Link</div>
                                                <div className="attachment-subtitle">{announcement.link}</div>
                                            </div>
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </article>
            </div>

            {/* Sticky Footer Stats */}
            <div className="detail-footer">
                {/* Views */}
                <div className="footer-stat">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#71717A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    <span>{viewCount} Views</span>
                </div>

                {/* Likes */}
                <div className="footer-stat">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill={likeCount > 0 ? '#ff4757' : 'none'} stroke={likeCount > 0 ? '#ff4757' : '#71717A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                    </svg>
                    <span>{likeCount} Likes</span>
                </div>

                {/* Share */}
                <button onClick={handleShare} className="footer-share">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3"></circle>
                        <circle cx="6" cy="12" r="3"></circle>
                        <circle cx="18" cy="19" r="3"></circle>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                    <span>{copied ? 'Copied' : 'Share'}</span>
                </button>
            </div>
        </div>
    );
};

export default AnnouncementDetail;
